import apiFetch from '@/utils/apiFetch';
import { defineStore } from 'pinia';
import DefaultConfig from '@/config/default';
import { parseCharge, formatCharge, parsePgNumericToString } from '@/utils/chargeHandler';
import type { Pagination } from '@/config/types/pagination';
import { Ledger, TopUpRequest } from '../config/types/responses';

// eslint-disable-next-line import/prefer-default-export
export const useBillingStore = defineStore('billing', {
  state: () => ({
    loading: false,
    error: null as string | null,
    pagination: {} as Pagination,
    lederResults: [] as Ledger[],
    topUpRequests: [] as TopUpRequest[],
    success: null as string | null,
    balance: null as number | null,
    balanceLoading: false,
  }),

  actions: {
    async fetchLedger(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/accounts/public/credit/ledger?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.lederResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async fetchAdminLedger(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/credit/ledger?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.lederResults = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    getFormattedAmount(charge: any): string {
      return parsePgNumericToString(charge);
    },
    getParsedCharge(charge: any): number {
      return parseCharge(charge).amount;
    },
    async createTopUpRequest(organizationId: string, amount: number | string, narration: string) {
      this.error = null;
      this.success = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');

      // Convert amount to number if it's a string
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/credit/requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ organizationId, amount: numericAmount, narration }),
        });
        const data = await res.json();
        if (!res.ok) {
          this.error = data.data.error || 'Error creating top-up request';
          return false;
        }
        this.success = 'Top-up request created successfully';
        return true;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateTopUpRequest(id: string, status: string) {
      this.error = null;
      this.success = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/credit/requests`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ id, status }),
        });
        const data = await res.json();
        if (!res.ok) {
          this.error = data.data.error || 'Error updating top-up request';
          return false;
        }
        this.success = `Top-up request ${status} successfully`;
        return true;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
        return false;
      } finally {
        this.loading = false;
      }
    },
    async fetchTopUpRequests(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/credit/requests?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.topUpRequests = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async fetchOrganizationBalance(organizationId: string) {
      this.balanceLoading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/admin/accounts/ledger/balance/${organizationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Error fetching balance');
        const data = await res.json();
        this.balance = data.balance ?? data.data?.balance ?? null;
      } catch (err: any) {
        this.balance = null;
      } finally {
        this.balanceLoading = false;
      }
    },

    async fetchPublicBalance() {
      this.balanceLoading = true;
      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/accounts/ledger/balance`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) throw new Error('Error fetching balance');
        const data = await res.json();
        this.balance = data.balance ?? data.data?.balance ?? null;
      } catch (err: any) {
        this.balance = null;
      } finally {
        this.balanceLoading = false;
      }
    },

    clearMessages() {
      this.error = null;
      this.success = null;
    },
  },

});
